import { clsx, styx } from "modules/Utils";
import { CSSProperties } from "react";
import Icon from "./Icon";
import Colors from "modules/Colors";
import Sty from "modules/Sty";
import ButtonSymphony from "./ButtonSymphony";
import { PlaybookStep } from "modules/Types";
import React from "react";
import useScreen from "Hooks/useScreen";

interface StepSymphonyProps {
    className: string;
    style?: CSSProperties;
    icon?: string;
    title?: string;
    description?: string;
    isActive?: boolean;
    children?: React.ReactNode;
    size?: 'small' | 'medium';
    isLastStep?: boolean;
    completed?: boolean;
    type?: 'number' | 'icon' | 'checkmark' | 'text';
    index?: number;
    color?: string;
    onClick?: () => void;
    buttonText?: string;
    hideLineIndicator?: boolean;
}

const StepSymphony = ({
    className,
    style,
    icon,
    title,
    description,
    isActive = false,
    children,
    size = 'medium',
    isLastStep = false,
    completed = false,
    type = 'icon',
    index = 0,
    color = Colors.greyLight,
    onClick,
    buttonText = 'Edit',
    hideLineIndicator = false
}: StepSymphonyProps) => {
    const { mobileView } = useScreen();

    const getIconSize = () => {
        switch (size) {
            case 'small':
                return 18;
            default:
                return 24;
        }
    }

    const getIcon = () => {
        if (completed) {
            return '✓'
        }

        switch (type) {
            case 'icon':
                if (typeof icon === 'string') {
                    return <Icon className="stepSymphonyIcon" icon={icon} size={getIconSize()} />
                }
                return icon;
            case 'checkmark':
                return '✓';
            case 'number':
                return index + 1;
            case 'text':
                return icon;
        }
    }

    return (
        <div
            className={clsx('stepSymphonyElement', className)}
            style={styx(sty.stepSymphonyElement, style)}
        >
            <div
                className="stepSymphonyTitleContainer"
                style={sty.stepSymphonyTitleContainer}
            >
                <div
                    className="stepSymphonyIcon"
                    style={styx(
                        sty.stepSymphonyIcon,
                        sty[`${size}_stepSymphonyIcon`],
                        completed ? { backgroundColor: Colors.purpleLighter } : { backgroundColor: color },
                    )}
                >
                    {getIcon()}
                </div>
                <div
                    className="stepSymphonyTitle"
                    style={styx(
                        Sty.text16bold,
                        sty[`${size}_stepSymphonyTitle`],
                        { whiteSpace: 'nowrap' }
                    )}
                >
                    {title}
                </div>
                <div style={{ width: '100%', height: 1, backgroundColor: Colors.greyLight }} />
                {onClick &&
                    <ButtonSymphony
                        className="stepSymphonyButton"
                        onClick={onClick}
                        variant="text"
                        style={{ marginLeft: 'auto' }}
                    >
                        {buttonText}
                    </ButtonSymphony>
                }
            </div>

            <div
                className="stepSymphonyContentContainer"
                style={styx(
                    sty.stepSymphonyContentContainer,
                    sty[`${size}_stepSymphonyContentContainer`],
                    mobileView ? { paddingLeft: 8 } : {},
                    isLastStep && { paddingBottom: 0 },
                    hideLineIndicator && { borderLeftWidth: 0, paddingLeft: 0, marginLeft: 0 }
                )}
            >
                <div className="stepSymphonyDescription" style={Sty.text16grey}>
                    {description}
                </div>
                {children}
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    stepSymphonyElement: {
        display: 'flex',
        flexDirection: 'column',
    },
    stepSymphonyTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    stepSymphonyIcon: {
        width: 32,
        height: 32,
        borderRadius: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: Colors.purple,
        flexShrink: 0,
    },
    small_stepSymphonyIcon: {
        width: 24,
        height: 24,
    },
    small_stepSymphonyTitle: {
        ...Sty.text14bold,
    },
    stepSymphonyContentContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingLeft: 24,
        marginLeft: 16,
        borderLeftWidth: 1,
        borderLeftColor: Colors.greyLight,
        borderLeftStyle: 'solid',
        paddingBottom: 42,
    },
    stepSymphonyContentContainer_isLastStep: {
        borderLeftColor: 'transparent',
        paddingBottom: 0,
    },
    small_stepSymphonyContentContainer: {
        paddingLeft: 16,
        marginLeft: 12,
        paddingBottom: 24,
    }
}

export default StepSymphony;